import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listarClientesZenPass,
  bloquearClienteZenPass,
  desbloquearClienteZenPass,
  bonificarLicencaZenPass,
  desbonificarLicencaZenPass,
  cancelarLicencaZenPass,
  reativarLicencaZenPass,
} from "../services/api";
import { Plus, ShieldOff, Unlock, Edit, Gift, Ban, RotateCcw, Users } from "lucide-react";
import BlockModal from "./common/BlockModal";
import CancelarLicencaModal from "./common/CancelarLicencaModal";
import BonificarLicencaModal from "./common/BonificarLicencaModal";
import { useFeedbackAlert } from "./common/useFeedbackAlert";

const DEFAULT_BLOCK_MESSAGE =
  "Entre em contato com o departamento financeiro: (11)5924-9068 Opção 3";

const ListaZenPass = ({ token }) => {
  const { showError, showSuccess, FeedbackAlert } = useFeedbackAlert();
  const navigate = useNavigate();
  const [itens, setItens] = useState([]);
  const [busca, setBusca] = useState("");
  const [showBlock, setShowBlock] = useState(false);
  const [showBonificar, setShowBonificar] = useState(false);
  const [showCancelar, setShowCancelar] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const carregar = async (search = busca) => {
    const r = await listarClientesZenPass(token, { search, cancelada: "" });
    const data = r?.data;
    setItens(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    carregar("").catch((err) => showError(err, "Erro ao listar ZenPass."));
  }, [token]);

  const bloquear = async (body) => {
    setLoading(true);
    try {
      await bloquearClienteZenPass(selected.id, body, token);
      setShowBlock(false);
      showSuccess("Licença bloqueada.");
      await carregar();
    } catch (err) {
      showError(err, "Erro ao bloquear.");
    } finally {
      setLoading(false);
    }
  };

  const acao = async (fn, ok) => {
    setLoading(true);
    try {
      await fn();
      setShowBonificar(false);
      setShowCancelar(false);
      showSuccess(ok);
      await carregar();
    } catch (err) {
      showError(err, "Erro na licença.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <FeedbackAlert />
      <div className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">ZenPass</h2>
          <button
            onClick={() => navigate("/licenciamento/zenpass/novo")}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-600 text-white"
          >
            <Plus size={16} /> Nova licença
          </button>
        </div>
        <input
          value={busca}
          onChange={(e) => {
            setBusca(e.target.value);
            carregar(e.target.value).catch(() => {});
          }}
          placeholder="Buscar nome, slug ou cliente"
          className="mb-4 w-full max-w-md px-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10 dark:text-white"
        />
        <div className="overflow-x-auto zen-card rounded-2xl">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="p-3">Cliente</th>
                <th className="p-3">Slug</th>
                <th className="p-3">Fila</th>
                <th className="p-3">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {itens.map((c) => (
                <tr key={c.id} className="border-t border-gray-100 dark:border-white/10">
                  <td className="p-3">{c.nomeFantasia}</td>
                  <td className="p-3">{c.slug}</td>
                  <td className="p-3">{c.maxFila}</td>
                  <td className="p-3">
                    {c.cancelada ? "Cancelada" : c.bonificada ? "Bonificada" : c.ativo ? "Ativa" : "Bloqueada"}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <button title="Usuários" onClick={() => navigate(`/licenciamento/zenpass/${c.id}/usuarios`)}>
                        <Users size={16} />
                      </button>
                      <button title="Editar" onClick={() => navigate(`/licenciamento/zenpass/editar/${c.id}`)}>
                        <Edit size={16} />
                      </button>
                      {c.ativo ? (
                        <button title="Bloquear" onClick={() => { setSelected(c); setShowBlock(true); }}>
                          <ShieldOff size={16} />
                        </button>
                      ) : (
                        <button
                          title="Desbloquear"
                          onClick={() => acao(() => desbloquearClienteZenPass(c.id, token), "Licença desbloqueada.")}
                        >
                          <Unlock size={16} />
                        </button>
                      )}
                      {!c.bonificada ? (
                        <button title="Bonificar" onClick={() => { setSelected(c); setShowBonificar(true); }}>
                          <Gift size={16} />
                        </button>
                      ) : (
                        <button
                          title="Desbonificar"
                          onClick={() => acao(() => desbonificarLicencaZenPass(c.id, token), "Bonificação removida.")}
                        >
                          <RotateCcw size={16} />
                        </button>
                      )}
                      {!c.cancelada && (
                        <button title="Cancelar" onClick={() => { setSelected(c); setShowCancelar(true); }}>
                          <Ban size={16} />
                        </button>
                      )}
                      {c.cancelada && (
                        <button
                          title="Reativar"
                          onClick={() => acao(() => reativarLicencaZenPass(c.id, token), "Licença reativada.")}
                        >
                          <RotateCcw size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <BlockModal
        show={showBlock}
        defaultMessage={DEFAULT_BLOCK_MESSAGE}
        onClose={() => setShowBlock(false)}
        onConfirm={bloquear}
      />
      <BonificarLicencaModal
        show={showBonificar}
        loading={loading}
        onClose={() => setShowBonificar(false)}
        onConfirm={(motivo) =>
          acao(() => bonificarLicencaZenPass(selected.id, { motivo }, token), "Licença bonificada.")
        }
      />
      <CancelarLicencaModal
        show={showCancelar}
        loading={loading}
        onClose={() => setShowCancelar(false)}
        onConfirm={(motivo) =>
          acao(() => cancelarLicencaZenPass(selected.id, { motivo }, token), "Licença cancelada.")
        }
      />
    </>
  );
};

export default ListaZenPass;
